import { Inject, Injectable, Optional } from '@angular/core';
import { SearchProvider } from './models/search-provider.model';
import { SEARCH_PROVIDER } from './tokens/search-provider.token';

/**
 * Holds the list of registered SearchProviders.
 * Providers are registered via the SEARCH_PROVIDER multi-provider token in
 * app.config.ts. The registry also supports lazy programmatic registration
 * via register() for providers that come online after app startup.
 */
@Injectable({ providedIn: 'root' })
export class SearchRegistryService {
  private providers = new Map<string, SearchProvider<unknown>>();

  constructor(
    @Optional() @Inject(SEARCH_PROVIDER) initial: SearchProvider<unknown>[] | null
  ) {
    (initial ?? []).forEach((p) => this.providers.set(p.id, p));
  }

  /** Late-register a provider (e.g. from a lazy-loaded route). */
  register(provider: SearchProvider<unknown>): void {
    this.providers.set(provider.id, provider);
  }

  unregister(providerId: string): void {
    this.providers.delete(providerId);
  }

  get(providerId: string): SearchProvider<unknown> | undefined {
    return this.providers.get(providerId);
  }

  /** All registered providers, optionally filtered to a whitelist. */
  getProviders(ids?: ReadonlyArray<string>): SearchProvider<unknown>[] {
    if (!ids || ids.length === 0) return [];
    const out: SearchProvider<unknown>[] = [];
    for (const id of ids) {
      const p = this.providers.get(id);
      if (p && (!p.isAvailable || p.isAvailable())) out.push(p);
    }
    return out;
  }

  knownProviderIds(): string[] {
    return Array.from(this.providers.keys());
  }
}
